import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DOMPurify from 'dompurify';
import type { UnsubscribeCampaignConfig } from '../../config/types';
import { unsubscribeSchema, UnsubscribeFormValues } from './UnsubscribeFormSchema';
import { useUnsubscribeSubmit } from './useUnsubscribeSubmit';
import RecaptchaWidget from '../shared/RecaptchaWidget';
import Modal from '../shared/Modal';
import {
  UNSUBSCRIBE_DEFAULT_TEXT_AFTER_FORM,
  UNSUBSCRIBE_DEFAULT_GDPR_INFO,
  UNSUBSCRIBE_DEFAULT_GDPR_INFO_HIDDEN,
} from '../shared/gdprBoilerplate';

interface Props {
  config: UnsubscribeCampaignConfig;
}

const sanitize = (html: string) => ({ __html: DOMPurify.sanitize(html) });

export default function UnsubscribeForm({ config }: Props) {
  const [expanded, setExpanded] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UnsubscribeFormValues>({ resolver: zodResolver(unsubscribeSchema) });
  const { onSubmit, isSubmitting, recaptchaRef, successOpen, setSuccessOpen, errorOpen, setErrorOpen } =
    useUnsubscribeSubmit(config);

  return (
    <form
      id="js-newsletter-form"
      className="form-container form-container--unsubscribe js-unsubscribe-form"
      data-form={config.slug}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Modal open={successOpen} setOpen={setSuccessOpen}>
        <b>Twoje zgłoszenie zostało przyjęte.</b>
      </Modal>

      <Modal open={errorOpen} setOpen={setErrorOpen}>
        Wystąpił błąd!
        <br />
        Prosimy spróbować później.
      </Modal>

      <div className="main-container-inner">
        <div className="skoda-header-holder">
          <h1 className="skoda-header-1" dangerouslySetInnerHTML={sanitize(config.infoTextHeader || '')} />
          <p className="skoda-text" dangerouslySetInnerHTML={sanitize(config.infoText || '')} />
        </div>

        <div className="skoda-form-holder">
          <div className="form-row">
            <label className="form-label form-label--dark">Adres - email*</label>
            <input type="email" className="form-input form-input--unsubscribe" {...register('email')} />
            {errors.email && <div className="form-error">{errors.email.message}</div>}
          </div>
        </div>

        <div dangerouslySetInnerHTML={sanitize(config.textAfterForm || UNSUBSCRIBE_DEFAULT_TEXT_AFTER_FORM)} />
      </div>

      <div style={{ background: '#f0f0f0' }}>
        <div className="main-container-inner">
          <div className="skoda-header-holder">
            <h2 className="skoda-header-2">Obowiązek informacyjny</h2>
          </div>
          <div dangerouslySetInnerHTML={sanitize(config.gdprInfo || UNSUBSCRIBE_DEFAULT_GDPR_INFO)} />
          <div className={`js-more-info${expanded ? '' : ' is-hidden'}`}>
            <div dangerouslySetInnerHTML={sanitize(config.gdprInfoHidden || UNSUBSCRIBE_DEFAULT_GDPR_INFO_HIDDEN)} />
          </div>
          <p className="skoda-text skoda-show-more">
            <br />
            <span
              className="item-show-more-btn js-btn-more-info"
              onClick={() => setExpanded((v) => !v)}
              role="button"
              tabIndex={0}
            >
              {expanded ? 'Zwiń' : 'Zobacz więcej informacji'}
            </span>
          </p>
        </div>
      </div>

      <div className="form-row" style={{ textAlign: 'center', marginTop: 20 }}>
        <button type="submit" className="button button--green" disabled={isSubmitting}>
          Wyślij wniosek
        </button>
      </div>

      <RecaptchaWidget ref={recaptchaRef} />
    </form>
  );
}
